import { type RequestType } from '@strata/react-chat';
import { SuggestSingleSelect } from '@strata/react-selects';
import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from 'store';
import { useOpenRequestCounts } from 'store/api/payerRequestApiThunks';
import { useGetPayersQuery, useGetSegmentsQuery } from 'store/api/referenceApi';
import { setPayerId, setSegmentId } from 'store/views/viewsSlice';

interface ViewsControlsProps {
    requestType?: Exclude<
        (typeof RequestType)[keyof typeof RequestType],
        typeof RequestType.ARCHIVE
    >;
}

export const ViewsControls = ({ requestType = undefined }: ViewsControlsProps) => {
    const dispatch = useAppDispatch();
    const { segmentIds } = useAppSelector((state) => state.auth);
    const { payerId, segmentId } = useAppSelector((state) => state.views);

    const { data: requestCounts } = useOpenRequestCounts(!requestType);

    const { data: payers, isFetching: isPayersFetching } = useGetPayersQuery();
    const { data: segments, isFetching: isSegmentsFetching } = useGetSegmentsQuery();

    const segmentOptions = useMemo(
        () => [
            { name: 'All Payer Segments', value: undefined, payerId: undefined },
            ...(segments ?? []).filter((segment) => segmentIds.includes(segment.segmentId)),
        ],
        [segmentIds, segments],
    );

    const payerOptions = useMemo(
        () =>
            (payers ?? [])
                .filter((payer) =>
                    segmentOptions?.find((segment) => segment.payerId === payer.payerId),
                )
                .map((payer) => ({
                    ...payer,
                    secondaryData: requestType
                        ? `(${requestCounts?.[payer.payerId]?.[requestType] ?? 0})`
                        : undefined,
                })),
        [payers, segmentOptions, requestCounts, requestType],
    );

    // On app initialize select first payer option
    useEffect(() => {
        if (!payerId) {
            dispatch(setPayerId({ payerId: payerOptions[0]?.payerId }));
        }
    }, [payerId, payerOptions, dispatch]);

    return (
        <div className="sds_views-controls">
            <SuggestSingleSelect
                id="payer"
                name="payer"
                label="Payer"
                required
                isLoading={isPayersFetching}
                values={payerOptions}
                selectedItem={payerOptions.find(({ value }) => value === payerId)}
                onChange={(newVal) => dispatch(setPayerId({ payerId: newVal?.value }))}
            />
            <SuggestSingleSelect
                id="segment"
                name="segment"
                label="Payer Segment"
                required
                isLoading={isSegmentsFetching}
                values={segmentOptions.filter(
                    (segment) => segment.payerId === payerId || segment.payerId === undefined,
                )}
                selectedItem={segmentOptions.find(({ value }) => value === segmentId)}
                onChange={(newVal) => dispatch(setSegmentId({ segmentId: newVal?.value }))}
            />
        </div>
    );
};
